import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiBriefcase,
  FiBookOpen,
  FiArrowLeft
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { auth, db } from "../../components/firebase";
import { useTheme } from "../../context/ThemeContext";
import { cn } from "../../lib/utils";
import logo from "../../assets/LOGO SnapED.png";

// Modern Input Component
const InputField = ({ label, icon: Icon, type, name, value, onChange, placeholder, error, isPassword, showPassword, togglePassword }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[13px] font-semibold text-muted-foreground ml-1">{label}</label>
    <div className="relative group">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm transition-colors group-focus-within:text-primary" />
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          "w-full pl-12 pr-4 py-3 bg-card border rounded-2xl text-foreground text-sm outline-none transition-all focus:ring-4 focus:ring-primary/10",
          error ? "border-rose-500" : "border-border focus:border-primary"
        )}
      />
      {isPassword && (
        <button
          type="button"
          onClick={togglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-none border-none text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
        >
          {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
        </button>
      )}
    </div>
    {error && <p className="text-rose-500 text-[11px] mt-1 ml-1 font-bold">{error}</p>}
  </div>
);

function Register({ isModal = false, onToggleMode }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    collegeName: "",
    role: "student"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const googleProvider = new GoogleAuthProvider();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = formData.role === 'company' ? "Rep name is required" : "Full name is required";
    if (!formData.collegeName.trim()) newErrors.collegeName = formData.role === 'company' ? "Company name is required" : "College is required";
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = "Valid email is required";
    if (formData.password.length < 6) newErrors.password = "Min 6 characters";
    if (!acceptedTerms) newErrors.terms = "Accept terms to continue";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      await sendEmailVerification(user);

      const normalizedRole = formData.role === 'company' ? 'company' : 'student';

      await setDoc(doc(db, "Users", user.uid), {
        uid: user.uid,
        email: user.email,
        fullName: formData.fullName,
        college: formData.collegeName,
        collegeName: formData.collegeName,
        role: normalizedRole,
        photo: "",
        emailVerified: false,
        createdAt: serverTimestamp(),
        provider: "email"
      });

      if (normalizedRole === 'company') {
        await setDoc(doc(db, "Companies", user.uid), {
          uid: user.uid,
          companyName: formData.collegeName,
          representativeName: formData.fullName,
          email: user.email,
          createdAt: serverTimestamp(),
          status: 'pending'
        });
      }

      toast.success("Account created! Check your email to verify.", { position: "top-center" });
      if (isModal) {
        onToggleMode(); // Switch to login in modal
      } else {
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (error) {
      toast.error(error.message, { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const userRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || "Google User",
          collegeName: "Not specified",
          role: "student",
          photo: user.photoURL || "",
          emailVerified: user.emailVerified,
          createdAt: serverTimestamp(),
          provider: "google"
        });
      }
      toast.success("Welcome! Signed in via Google.", { position: "top-center" });
      const from = location.state?.from;
      const redirectPath = from || "/home";
      setTimeout(() => navigate(redirectPath), 1500);
    } catch (error) {
      toast.error("Google sign up failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  return (
    <div className={cn(
      "min-h-screen flex items-center justify-center p-5 relative overflow-hidden font-sans transition-colors duration-300",
      isModal ? "min-h-auto bg-transparent p-0" : "bg-background"
    )}>
      {/* Background elements - hidden if modal */}
      {!isModal && (
        <>
          <div className="absolute inset-0 z-0">
            <motion.div
              animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] bg-primary/10 blur-[120px] rounded-full opacity-30"
            />
          </div>

          <motion.button
            onClick={() => navigate('/')}
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
            className="absolute top-8 left-8 flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-foreground text-sm font-semibold cursor-pointer z-10 backdrop-blur-md hover:bg-accent transition-colors shadow-sm"
          >
            <FiArrowLeft /> Back to Home
          </motion.button>
        </>
      )}

      <motion.div
        initial={isModal ? {} : { opacity: 0, scale: 0.95 }}
        animate={isModal ? {} : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-[440px] z-10"
      >
        <div className="text-center mb-8">
          {!isModal && (
            <Link to="/">
              <img src={logo} alt="SnapED" className="h-10 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(34,197,94,0.2)]" />
            </Link>
          )}
          <h1 className="text-3xl font-black text-foreground mb-2 tracking-tight">
            Get Started
          </h1>
          <p className="text-muted-foreground text-[15px] font-medium opacity-80">
            Create your account to join SnapED
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-2xl rounded-[2rem] p-8 border border-border/60 shadow-2xl">
          {/* Role Selection */}
          <div className="flex gap-3 mb-6">
            <button
              type="button"
              onClick={() => setFormData(p => ({ ...p, role: 'student' }))}
              className={cn(
                "flex-1 p-3 rounded-2xl text-[13px] font-bold cursor-pointer flex items-center justify-center gap-2 transition-all border-2",
                formData.role === 'student'
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-transparent border-border/60 text-muted-foreground"
              )}
            >
              <FiBookOpen size={16} /> Student
            </button>
            <button
              type="button"
              onClick={() => setFormData(p => ({ ...p, role: 'company' }))}
              className={cn(
                "flex-1 p-3 rounded-2xl text-[13px] font-bold cursor-pointer flex items-center justify-center gap-2 transition-all border-2",
                formData.role === 'company'
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-transparent border-border/60 text-muted-foreground"
              )}
            >
              <FiBriefcase size={16} /> Company
            </button>
          </div>

          <form onSubmit={handleRegister} className="flex flex-col gap-4">
            <InputField
              label={formData.role === 'company' ? "Representative Name" : "Full Name"}
              icon={FiUser}
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder={formData.role === 'company' ? "Your name" : "John Doe"}
              error={errors.fullName}
            />

            <InputField
              label={formData.role === 'company' ? "Company Name" : "College Name"}
              icon={formData.role === 'company' ? FiBriefcase : FiBookOpen}
              name="collegeName"
              value={formData.collegeName}
              onChange={handleChange}
              placeholder={formData.role === 'company' ? "Org name" : "GGSIPU"}
              error={errors.collegeName}
            />

            <InputField
              label="Email Address"
              icon={FiMail}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="name@example.com"
              error={errors.email}
            />

            <InputField
              label="Password"
              icon={FiLock}
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              isPassword
              showPassword={showPassword}
              togglePassword={() => setShowPassword(!showPassword)}
              error={errors.password}
            />

            {/* Terms */}
            <div className="space-y-2 px-1">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-[12px] text-muted-foreground leading-relaxed font-medium">
                  I agree to the <Link to="/terms & condition" className="text-primary font-bold hover:underline">Terms</Link> and <Link to="/privacy-policy" className="text-primary font-bold hover:underline">Privacy Policy</Link>
                </span>
              </label>
              {errors.terms && <p className="text-rose-500 text-[11px] font-bold ml-6">{errors.terms}</p>}
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-2 py-4 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground text-[15px] font-black border-none cursor-pointer flex items-center justify-center gap-2.5 shadow-xl shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating..." : <>Sign Up <FiArrowRight size={18} /></>}
            </motion.button>

            <div className="flex items-center gap-4 my-1">
              <div className="flex-1 h-px bg-border/60" />
              <span className="text-muted-foreground text-[10px] font-black uppercase tracking-[0.2em] opacity-60">OR</span>
              <div className="flex-1 h-px bg-border/60" />
            </div>

            <motion.button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="py-3.5 rounded-2xl bg-background/50 border border-border text-foreground text-sm font-bold cursor-pointer flex items-center justify-center gap-3 transition-all hover:bg-background hover:border-primary/40 disabled:opacity-50"
            >
              <FcGoogle size={22} /> Join with Google
            </motion.button>
          </form>

          <p className="text-center mt-8 text-sm text-muted-foreground font-medium m-0">
            Already have an account?{" "}
            {isModal ? (
              <button
                onClick={onToggleMode}
                className="text-primary font-black cursor-pointer text-[15px] px-2 py-1 rounded-lg hover:bg-primary/10 transition-colors"
              >
                Sign In
              </button>
            ) : (
              <Link to="/login" state={{ from: location.state?.from }} className="text-primary font-black hover:underline">Sign In</Link>
            )}
          </p>
        </div>
      </motion.div>
      {!isModal && <ToastContainer theme={isDarkMode ? "dark" : "light"} limit={1} position="top-center" />}
    </div>
  );
}

export default Register;